import {
  ALL_SPORTS,
  DEFAULT_RANGE,
  GAMES_STAT_ABBR,
  MAX_SELECTED_STATS,
} from '../constants'
import { GAMES_COLOR_SLOT } from '../constants/colors'
import { getSportKey } from './sportKey'
import type {
  ProgressSport,
  StatsRole,
  StatsSelection,
  StatsSelectionAction,
} from '../types'

export const INITIAL_SELECTION: StatsSelection = {
  sportKey: null,
  role: 'skater',
  range: DEFAULT_RANGE,
  stats: [],
  colorSlots: {},
  openSeasonRowId: null,
}

/** Any stat but Games Played, which only ever draws alongside one. */
const isLineStat = (stat: string): boolean => stat !== GAMES_STAT_ABBR

/**
 * Still-offered stats stay, in selection order; with no line stat among them,
 * the defaults (`pickProgressDefaults`) that are offered, else the left-most
 * line stat's chip.
 */
const resolveStats = (
  stats: string[],
  chartStats: string[],
  defaultStats: string[]
): string[] => {
  const kept = stats.filter((stat) => chartStats.includes(stat))
  if (kept.some(isLineStat)) {
    return kept
  }
  const defaults = defaultStats.filter((stat) => chartStats.includes(stat))
  if (defaults.some(isLineStat)) {
    return defaults
  }
  const firstLineStat = chartStats.find(isLineStat)
  return firstLineStat ? [...kept, firstLineStat] : []
}

/** No more line stats can be added — one per colour, green held back. */
export const isStatLimitReached = (stats: string[]): boolean =>
  stats.filter(isLineStat).length >= MAX_SELECTED_STATS - 1

/** At the limit an unselected chip locks; Games Played has its own colour. */
export const isStatLocked = (stats: string[], stat: string): boolean =>
  isLineStat(stat) && !stats.includes(stat) && isStatLimitReached(stats)

const lowestFreeSlot = (taken: Set<number>): number => {
  let slot = 0
  while (taken.has(slot)) {
    slot += 1
  }
  return slot
}

/**
 * A colour slot for each of `stats`: Games Played is always green; any other
 * stat keeps its stored slot, and one without a slot (e.g. the default before
 * any click) takes the lowest free slot, in selection order. Slots of stats not
 * in `stats` are dropped.
 */
const resolveColorSlots = (
  stats: string[],
  colorSlots: Record<string, number>
): Record<string, number> => {
  const resolved = new Map<string, number>()
  const taken = new Set<number>([GAMES_COLOR_SLOT])
  stats.forEach((stat) => {
    if (!isLineStat(stat)) {
      resolved.set(stat, GAMES_COLOR_SLOT)
      return
    }
    const slot = colorSlots[stat]
    if (slot !== undefined && !taken.has(slot)) {
      resolved.set(stat, slot)
      taken.add(slot)
    }
  })
  stats.forEach((stat) => {
    if (!resolved.has(stat)) {
      const slot = lowestFreeSlot(taken)
      resolved.set(stat, slot)
      taken.add(slot)
    }
  })
  return Object.fromEntries(resolved)
}

/**
 * Adds an unselected stat at the end, on the lowest free colour slot — unless
 * it is locked at the limit. Removes a selected one unless that leaves no line
 * stat (Games Played never draws alone); its slot frees up and every other
 * stat keeps its own. Toggles from the resolved stats, so the shown
 * default is kept and stats no longer offered (and their slots) are dropped.
 */
const toggleStat = (
  state: StatsSelection,
  stat: string,
  chartStats: string[],
  defaultStats: string[]
): StatsSelection => {
  const current = resolveStats(state.stats, chartStats, defaultStats)
  const currentSlots = resolveColorSlots(current, state.colorSlots)
  if (!current.includes(stat)) {
    if (isStatLocked(current, stat)) {
      return state
    }
    const stats = [...current, stat]
    return {
      ...state,
      stats,
      colorSlots: resolveColorSlots(stats, currentSlots),
    }
  }
  const stats = current.filter((s) => s !== stat)
  if (!stats.some(isLineStat)) {
    return state
  }
  return { ...state, stats, colorSlots: resolveColorSlots(stats, currentSlots) }
}

export const statsSelectionReducer = (
  state: StatsSelection,
  action: StatsSelectionAction
): StatsSelection => {
  switch (action.type) {
    case 'setSport':
      return {
        ...state,
        sportKey: action.sportKey,
        stats: [],
        colorSlots: {},
        openSeasonRowId: null,
      }
    case 'setRole':
      return {
        ...state,
        role: action.role,
        stats: [],
        colorSlots: {},
        openSeasonRowId: null,
      }
    case 'setRange':
      return { ...state, range: action.range }
    case 'toggleStat':
      return toggleStat(
        state,
        action.stat,
        action.chartStats,
        action.defaultStats
      )
    case 'toggleSeason':
      return {
        ...state,
        openSeasonRowId:
          state.openSeasonRowId === action.rowId ? null : action.rowId,
      }
    case 'reset':
      return INITIAL_SELECTION
  }
}

interface ResolveStatsSelectionArgs {
  selection: StatsSelection
  sports: ProgressSport[]
  roles: StatsRole[]
  chartStats: string[]
  /** What to chart before the user picks; [] falls back to the first chip. */
  defaultStats?: string[]
}

const resolveSport = (
  sportKey: string | null,
  sports: ProgressSport[]
): string | null => {
  if (
    sportKey !== null &&
    (sportKey === ALL_SPORTS || sports.some((s) => getSportKey(s) === sportKey))
  ) {
    return sportKey
  }
  return sports[0] ? getSportKey(sports[0]) : null
}

export const resolveStatsSelection = ({
  selection,
  sports,
  roles,
  chartStats,
  defaultStats = [],
}: ResolveStatsSelectionArgs): StatsSelection => {
  const role = roles.includes(selection.role)
    ? selection.role
    : roles[0] ?? 'skater'
  const stats = resolveStats(selection.stats, chartStats, defaultStats)
  return {
    ...selection,
    sportKey: resolveSport(selection.sportKey, sports),
    role,
    stats,
    colorSlots: resolveColorSlots(stats, selection.colorSlots),
  }
}
